import { ThemeEvents } from '@theme/events';
import {
  removeFromCart,
  heartbeat,
  getCartToken,
  validateCart,
  freezeCart,
  formatTicketError,
  normalizeCartToken,
} from '@theme/ticket-cart-api';
import { showTicketToast } from '@theme/ticket-toast';
import '@theme/ticket-countdown'; // enregistre le custom element <ticket-countdown>

/**
 * Gestionnaire global du cycle de vie des lignes ticket dans le panier (Phases 8 + 10-lite).
 *
 * Chargé globalement (cf. layout/theme.liquid) car le panier (drawer) peut être
 * manipulé depuis n'importe quelle page. Trois rôles :
 *
 *  1. **Réconciliation (suppression)** : à chaque `cart:update`, on compare les
 *     réservations ticket présentes au panier (`item.properties._reservation_id`)
 *     à celles qu'on connaissait ; toute réservation disparue → `removeFromCart`
 *     (libère le lock Recreatex, quel que soit le mode de suppression : trash,
 *     qty 0, bouton « Modifier »). Sinon le lock resterait actif et bloquerait
 *     de la capacité jusqu'à son expiration.
 *
 *  2. **Heartbeat** : tant qu'il y a ≥1 ligne ticket et que l'onglet est visible,
 *     `heartbeat({ cart_token })` toutes les ~5 min pour prolonger les locks
 *     (l'app plafonne à locked_at + 30 min — le countdown suit `next_expires_at`).
 *
 *  3. **Bouton « Modifier »** : sur les lignes ticket (quantité figée), un bouton
 *     `[data-ticket-modify]` redirige vers la fiche produit en mode édition
 *     (`?date=…&action=update`) — quantités du jour préremplies, submit =
 *     remplacement du groupe (lignes + locks). Rien n'est retiré avant le submit.
 *
 * Quantité figée au panier : pas d'édition in-cart (cf. plans/selling-tickets.md
 * Phase 10) — la limite de `change.js` sur les line item properties empêche un
 * relock propre. Pour changer : « Modifier » → re-réserver.
 */

const HEARTBEAT_INTERVAL_MS = 5 * 60 * 1000;
// Beat aussi au chargement de page (throttlé) : le setInterval repart de zéro à
// chaque navigation, donc sans beat-au-load un utilisateur qui navigue (pages
// < 5 min) n'enverrait JAMAIS de heartbeat et ses locks expireraient à ~20 min
// malgré son activité. Le beat-au-load resynchronise aussi les countdowns
// (la property Shopify `_expires_at` reste figée à la valeur de l'ajout).
const HEARTBEAT_LOAD_THROTTLE_MS = 60 * 1000;
const HEARTBEAT_LAST_KEY = 'ticket-heartbeat-last';

class TicketCartManager {
  /** @type {Set<string>} — reservation_id des lignes ticket actuellement au panier */
  #knownReservations = new Set();
  /** @type {string | null} */
  #cartToken = null;
  /** @type {ReturnType<typeof setInterval> | null} */
  #timer = null;
  /** @type {boolean} */
  #syncing = false;
  /** @type {boolean} — re-sync demandé pendant qu'un sync était en cours */
  #pendingSync = false;
  /** @type {boolean} — anti double-déclenchement du flux checkout */
  #checkingOut = false;
  /** @type {boolean} — refresh du blocage déjà programmé (debounce rAF) */
  #refreshScheduled = false;

  init() {
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    document.addEventListener('visibilitychange', this.#onVisibility);
    document.addEventListener('ticket:expired', this.#onExpired);
    // Capture : on intercepte avant le handler natif (bouton « Modifier » + blocage checkout).
    document.addEventListener('click', this.#onClick, true);
    document.addEventListener('submit', this.#onSubmitCapture, true);
    // Le morph des sections (Section Rendering API) réécrit `disabled`/`hidden`
    // depuis le HTML serveur (toujours « débloqué ») APRÈS nos handlers — on
    // ré-applique donc l'état de blocage à chaque mutation du DOM. Idempotent :
    // écrire la même valeur de propriété ne produit pas de mutation → pas de
    // boucle avec l'observer.
    const observer = new MutationObserver(this.#scheduleRefresh);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['disabled', 'hidden', 'class'],
    });
    // Refresh immédiat (les countdowns sont upgradés à l'import, leurs classes
    // sont posées) : réactive sans attendre le fetch de #sync le bouton rendu
    // `disabled` par défaut quand les tickets sont valides.
    this.#refreshCheckoutBlock();
    this.#sync();
  }

  #onCartUpdate = () => {
    this.#sync();
  };

  #onVisibility = () => {
    this.#updateHeartbeat();
  };

  #scheduleRefresh = () => {
    if (this.#refreshScheduled) return;
    this.#refreshScheduled = true;
    requestAnimationFrame(() => {
      this.#refreshScheduled = false;
      this.#refreshCheckoutBlock();
    });
  };

  /**
   * Lit `/cart.js`, réconcilie les locks orphelins et (re)cadence le heartbeat.
   */
  async #sync() {
    // Un sync en vol : on note qu'il faudra repartir (l'état du panier a pu
    // changer entre-temps) plutôt que de jeter silencieusement la demande.
    if (this.#syncing) {
      this.#pendingSync = true;
      return;
    }
    this.#syncing = true;

    try {
      const cart = await this.#fetchCart();
      if (!cart) return;

      this.#cartToken = normalizeCartToken(cart.token) ?? this.#cartToken;

      /** @type {Set<string>} */
      const current = new Set();
      for (const item of cart.items ?? []) {
        const rid = item?.properties?._reservation_id;
        if (rid != null && String(rid) !== '') current.add(String(rid));
      }

      // Réservations disparues depuis le dernier sync → libérer le lock.
      if (this.#cartToken) {
        for (const reservation_id of this.#knownReservations) {
          if (!current.has(reservation_id)) {
            removeFromCart({ cart_token: this.#cartToken, reservation_id });
          }
        }
      }

      this.#knownReservations = current;
      this.#updateHeartbeat();
      this.#beatIfStale();
      this.#refreshCheckoutBlock();
    } finally {
      this.#syncing = false;
      if (this.#pendingSync) {
        this.#pendingSync = false;
        this.#sync();
      }
    }
  }

  /** Beat immédiat (throttlé) au chargement / sync — cf. HEARTBEAT_LOAD_THROTTLE_MS. */
  #beatIfStale() {
    if (this.#knownReservations.size === 0 || document.hidden) return;
    let last = 0;
    try {
      last = Number(localStorage.getItem(HEARTBEAT_LAST_KEY)) || 0;
    } catch {
      // localStorage indisponible (navigation privée stricte) → beat sans throttle.
    }
    if (Date.now() - last > HEARTBEAT_LOAD_THROTTLE_MS) this.#beat();
  }

  /** @returns {Promise<any | null>} */
  async #fetchCart() {
    try {
      const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      return await response.json();
    } catch (error) {
      console.error('[ticket-cart] /cart.js failed', error);
      return null;
    }
  }

  #updateHeartbeat() {
    const shouldRun = this.#knownReservations.size > 0 && !document.hidden;

    if (shouldRun && !this.#timer) {
      this.#timer = setInterval(this.#beat, HEARTBEAT_INTERVAL_MS);
    } else if (!shouldRun && this.#timer) {
      clearInterval(this.#timer);
      this.#timer = null;
    }
  }

  #beat = async () => {
    if (!this.#cartToken) return;
    try {
      localStorage.setItem(HEARTBEAT_LAST_KEY, String(Date.now()));
    } catch {
      /* non bloquant */
    }
    const res = await heartbeat({ cart_token: this.#cartToken });
    // Le heartbeat prolonge le lock serveur (plafonné à locked_at + 30 min côté
    // app) → on repousse les countdowns pour qu'ils n'expirent pas à tort
    // pendant la navigation active. Et si un countdown était passé « expiré » à
    // tort (property périmée), l'extension doit ré-autoriser le checkout (sinon
    // réévalué seulement sur cart:update). Contrat app : `next_expires_at` =
    // minimum des échéances actives, toujours présent quand quelque chose a été
    // prolongé — s'il manque, rien n'a été prolongé (plafond atteint ou locks
    // expirés serveur) : on ne « ressuscite » pas les countdowns.
    if (res?.ok) {
      const next = res.data?.next_expires_at;
      // Garde : une « prolongation » ne peut pas être dans le passé. Vu en réel :
      // l'app peut renvoyer le minimum d'un vieux lock expiré du même cart_token
      // (jamais désenregistré) — l'appliquer tuerait à tort le countdown d'un
      // lock frais et bloquerait le checkout.
      const nextMs = next ? new Date(next).getTime() : NaN;
      if (!Number.isNaN(nextMs) && nextMs > Date.now()) {
        this.#extendCountdowns(next);
        this.#refreshCheckoutBlock();
      }
    }
  };

  /**
   * Repousse l'échéance affichée par les `<ticket-countdown>`.
   * @param {string} nextExpiresAt — ISO (validé par l'appelant) renvoyé par l'app
   */
  #extendCountdowns(nextExpiresAt) {
    for (const el of document.querySelectorAll('ticket-countdown')) {
      el.setAttribute('data-expires-at', nextExpiresAt);
    }
  }

  #onExpired = () => {
    this.#setCheckoutBlocked(true);
  };

  /**
   * Bloque/débloque le checkout selon la présence de countdowns expirés.
   * Panier à tickets : le bouton est rendu `disabled` par défaut côté Liquid
   * (`data-ticket-gated`, cf. cart-summary.liquid) — c'est ici qu'il est
   * réactivé une fois les countdowns vérifiés (tous valides).
   */
  #refreshCheckoutBlock() {
    // Pendant validate/freeze, le bouton est désactivé par #setCheckoutLoading :
    // ne pas le réactiver sous les pieds du flux de paiement.
    if (this.#checkingOut) return;
    const expired = this.#hasExpired();
    // Panier sans ticket : ne rien toucher — on ne doit pas réactiver un bouton
    // désactivé pour d'autres raisons (panier vide rendu `disabled` par Liquid).
    if (!expired && !document.querySelector('ticket-countdown, [data-ticket-gated]')) return;
    this.#setCheckoutBlocked(expired);
  }

  /** @param {boolean} blocked */
  #setCheckoutBlocked(blocked) {
    for (const btn of document.querySelectorAll('button[name="checkout"], #checkout')) {
      if (btn instanceof HTMLButtonElement) btn.disabled = blocked;
    }
    for (const banner of document.querySelectorAll('[data-ticket-expired-banner]')) {
      if (banner instanceof HTMLElement) banner.hidden = !blocked;
    }
  }

  /** @param {Event} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    if (target.closest('button[name="checkout"], #checkout')) {
      this.#interceptCheckout(event);
      return;
    }

    const trigger = target.closest('[data-ticket-modify]');
    if (!trigger) return;
    event.preventDefault();
    this.#handleModify(trigger);
  };

  /** Intercepte la soumission du form panier (= checkout). @param {Event} event */
  #onSubmitCapture = (event) => {
    const form = event.target;
    if (form instanceof HTMLFormElement && form.id === 'cart-form') {
      this.#interceptCheckout(event);
    }
  };

  /**
   * Phase 11 — passage en caisse : on ne touche que si le panier contient des
   * tickets. Sinon checkout normal (bons cadeaux, etc.). Si expiré → bloqué ;
   * sinon validate → freeze → /checkout.
   * @param {Event} event
   */
  #interceptCheckout(event) {
    // Présence DOM de <ticket-countdown> en secours de #knownReservations : au
    // tout premier clic, le sync initial (async) peut ne pas avoir encore peuplé
    // les réservations — on ne doit jamais laisser passer un panier à tickets.
    const hasTickets = this.#hasTicketLines() || !!document.querySelector('ticket-countdown');
    if (!hasTickets) return; // pas de ticket → laisser passer

    event.preventDefault();
    event.stopPropagation();

    if (this.#hasExpired()) {
      this.#setCheckoutBlocked(true);
      // Message explicite même si le clic est parti pendant une fenêtre où le
      // morph venait de réactiver le bouton (le bandeau seul peut être hors
      // du viewport dans le drawer).
      const banner = document.querySelector('[data-ticket-expired-banner]');
      const message = (banner?.textContent ?? '').trim();
      showTicketToast(message || 'Votre réservation a expiré. Veuillez resélectionner vos tickets.', {
        type: 'error',
      });
      return;
    }
    this.#startCheckout();
  }

  async #startCheckout() {
    if (this.#checkingOut) return;
    this.#checkingOut = true;
    this.#setCheckoutLoading(true);

    const onError = (message) => {
      this.#setCheckoutLoading(false);
      this.#checkingOut = false;
      showTicketToast(message || this.#checkoutLabel('i18nError', 'Le paiement n’a pas pu démarrer, veuillez réessayer.'), {
        type: 'error',
      });
    };

    try {
      const cartToken = this.#cartToken || (await getCartToken());
      if (!cartToken) return onError(null);

      // 1) Re-validation Recreatex (locks vivants + règles panier-entier).
      const v = await validateCart({ cart_token: cartToken });
      if (!v.ok || !v.data?.success) {
        const code = v.data?.error_code || v.error_code || '';
        if (typeof code === 'string' && code.includes('Expired')) this.#refreshCheckoutBlock();
        return onError(formatTicketError(code) || v.data?.message || v.message);
      }

      // 2) Gel des locks pour la durée du paiement.
      const f = await freezeCart({ cart_token: cartToken });
      if (!f.ok || !f.data?.success) {
        const code = f.data?.error_code || f.error_code || '';
        return onError(formatTicketError(code) || f.data?.message || f.message);
      }

      // 3) OK → checkout (le loader reste actif pendant la redirection).
      window.location.href = '/checkout';
    } catch (error) {
      console.error('[ticket-cart] checkout flow failed', error);
      onError(null);
    }
  }

  /** @param {boolean} value */
  #setCheckoutLoading(value) {
    for (const btn of document.querySelectorAll('button[name="checkout"], #checkout')) {
      if (!(btn instanceof HTMLButtonElement)) continue;
      btn.disabled = value;
      btn.classList.toggle('cart__checkout-button--loading', value);
      if (value) {
        if (!btn.dataset.originalLabel) btn.dataset.originalLabel = btn.textContent.trim();
        btn.textContent = this.#checkoutLabel('i18nValidating', 'Vérification…');
      } else if (btn.dataset.originalLabel) {
        btn.textContent = btn.dataset.originalLabel;
      }
    }
  }

  /**
   * Lit un libellé i18n posé sur le bouton checkout (`data-i18n-*`).
   * @param {string} datasetKey
   * @param {string} fallback
   * @returns {string}
   */
  #checkoutLabel(datasetKey, fallback) {
    const btn = document.querySelector('button[name="checkout"], #checkout');
    const value = btn instanceof HTMLElement ? btn.dataset[datasetKey] : undefined;
    return value && value.trim() !== '' ? value : fallback;
  }

  /** @returns {boolean} */
  #hasTicketLines() {
    return this.#knownReservations.size > 0;
  }

  /**
   * Au moins une ligne ticket expirée ? Lit `data-expires-at` (la donnée) et pas
   * seulement la classe `--expired` : un élément pas encore upgradé (course à
   * l'init, module countdown en échec) doit quand même compter comme expiré —
   * sinon le refresh réactiverait le bouton gaté sur la foi d'un DOM muet.
   * @returns {boolean}
   */
  #hasExpired() {
    for (const el of document.querySelectorAll('ticket-countdown')) {
      if (el.classList.contains('ticket-countdown--expired')) return true;
      const target = new Date(el.getAttribute('data-expires-at') ?? '').getTime();
      if (!Number.isNaN(target) && target <= Date.now()) return true;
    }
    return false;
  }

  /**
   * « Modifier » : redirection immédiate vers la fiche produit en mode édition
   * (`?date=…&action=update`). Rien n'est retiré ici : le panier reste intact si
   * l'utilisateur abandonne ; le remplacement (lignes + locks du jour) se fait au
   * submit du booking form (cf. ticket-booking-form.js, mode update).
   * @param {Element} trigger
   */
  #handleModify(trigger) {
    const productUrl = trigger.getAttribute('data-product-url') || '/';
    const date = trigger.getAttribute('data-date') || '';

    const url = new URL(productUrl, window.location.origin);
    if (date) url.searchParams.set('date', date);
    url.searchParams.set('action', 'update');
    window.location.href = url.toString();
  }
}

if (!window.__ticketCartManager) {
  const manager = new TicketCartManager();
  window.__ticketCartManager = manager;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => manager.init(), { once: true });
  } else {
    manager.init();
  }
}
